import * as z from "zod/v4";

// =============================================================================
// TCR (The Campaign Registry) — shared schemas for Telnyx 10DLC brand and
// campaign registration. Any SMS product that provisions a dedicated 10DLC
// number composes these into its onboarding schemas so merchants capture the
// fields once regardless of product (Recover today; Support, Marketing later).
//
// All fields are optional on the schema level. Defaults (below) are applied
// server-side before submission to Telnyx — merchants can accept defaults
// without touching these form controls.
// =============================================================================

export const TcrBusinessType = z.enum([
  "PRIVATE_PROFIT",
  "PUBLIC_PROFIT",
  "NON_PROFIT",
  "GOVERNMENT",
  "SOLE_PROPRIETOR",
]);
export type TcrBusinessType = z.infer<typeof TcrBusinessType>;

export const TcrIndustryVertical = z.enum([
  "RETAIL",
  "ECOMMERCE",
  "FOOD_AND_DRINK",
  "FASHION",
  "HEALTH_AND_BEAUTY",
  "FITNESS",
  "HOBBIES",
  "HOME_AND_GARDEN",
  "OTHER",
]);
export type TcrIndustryVertical = z.infer<typeof TcrIndustryVertical>;

export const TcrStockExchange = z.enum(["NYSE", "NASDAQ", "AMEX", "OTHER"]);
export type TcrStockExchange = z.infer<typeof TcrStockExchange>;

// -----------------------------------------------------------------------------
// Brand registration fields — team-scoped, registered once per merchant and
// reused across all their SMS use cases.
// -----------------------------------------------------------------------------

export const TcrBrandFieldsSchema = z.object({
  businessType: TcrBusinessType.optional(),
  industryVertical: TcrIndustryVertical.optional(),
  contactJobTitle: z.string().min(1).optional(),
  isPubliclyTraded: z.boolean().optional(),
  stockSymbol: z.string().optional(),
  stockExchange: TcrStockExchange.optional(),
});
export type TcrBrandFields = z.infer<typeof TcrBrandFieldsSchema>;

export const DEFAULT_TCR_BRAND_FIELDS: TcrBrandFields = {
  businessType: "PRIVATE_PROFIT",
  industryVertical: "RETAIL",
  contactJobTitle: "Owner",
  isPubliclyTraded: false,
};

// -----------------------------------------------------------------------------
// Campaign-level compliance fields. TCR requires 2–5 distinct sample
// messages. The outreach template already covers the initial message, so the
// two additional samples below bring the total to 3.
// -----------------------------------------------------------------------------

export const TcrCampaignFieldsSchema = z.object({
  sampleMessageFollowUp: z.string().max(320).optional(),
  sampleMessageDiscountDelivery: z.string().max(320).optional(),
});
export type TcrCampaignFields = z.infer<typeof TcrCampaignFieldsSchema>;

export const DEFAULT_TCR_CAMPAIGN_FIELDS: TcrCampaignFields = {
  sampleMessageFollowUp:
    "Still thinking it over? I've set aside {discount_code} for you — reply BUY anytime.",
  sampleMessageDiscountDelivery:
    "Here's your discount: {discount_code}. Apply at checkout: {discount_link}",
};
